/**
 * c-search-quote.js
 *
 * 用の検索クエリ前処理。
 * 検索キーワード内の既存ダブルクォート (") をすべて除去した上で、
 * スペース区切りで単語分割し、各単語をダブルクォートで囲んだ
 * フレーズ検索クエリに自動変換する。
 *
 * 例:
 *   q=冷感               → q="冷感"
 *   q=冷感 接触          → q="冷感" "接触"
 *   q="冷感" (既存)      → " 除去 → q="冷感"
 *   q="冷感" "接触" 余分 → " 除去 → q="冷感" "接触" "余分"
 *
 * 適用パス:
 *   ① 検索フォーム submit 時 (<form action="/search"> 系)
 *      - PC ヘッダー検索 / SP ドロワー検索 / search-modal 内フォーム 等
 *      - form のデフォルト送信を preventDefault で阻止し、手動で URL 構築 → location.href 遷移
 *      - input.value 自体は変更しないため、ユーザーから見える検索欄の表示は "冷感" のままにならず
 *        元の「冷感」が維持される (フォーム再表示時も同様)
 *   ② /search ページ訪問時の URL 直接アクセス (TOP バナー等の固定リンクから q=xxx で遷移)
 *      - URL の q が quoteTerms 適用結果と異なれば quote 済 URL に置き換えて location.replace() で再リクエスト
 *      - リダイレクト不要時は、検索フォーム input[name="q"] の表示値からダブルクォートを除去 (見た目は元の単語のまま)
 *
 * 効果:
 *   - Shopify 検索の部分一致挙動 (例: 「冷感」検索で「冷え」「冷たい」等の関連語に
 *     マッチしてしまう問題) を回避し、入力単語の完全フレーズ一致のみで検索する。
 *   - 公式の検索クエリ構文 (Phrase Queries) を活用 (https://shopify.dev/docs/api/usage/search-syntax)
 *   - 内部的なクエリは "冷感" 形式だが、ユーザー視点では検索欄に「冷感」が表示されたまま
 *
 * 関連: PM 指摘 #11 (TOP「冷感」検索結果の不要商品除外 → スパッツサポーター除外目的)
 */
(function () {
  'use strict';

  function isSearchForm(form) {
    if (!form || form.tagName !== 'FORM') return false;
    var action = form.getAttribute('action') || '';
    return action === '/search' || action.indexOf('/search') !== -1;
  }

  // 既存の " をすべて除去し、空白で単語分割 → 各単語を " で囲む
  function quoteTerms(value) {
    if (typeof value !== 'string') return value;
    var cleaned = value.replace(/"/g, '').trim();
    if (cleaned.length === 0) return cleaned;

    var words = cleaned.split(/\s+/).filter(function (w) {
      return w.length > 0;
    });
    if (words.length === 0) return cleaned;

    return words
      .map(function (w) {
        return '"' + w + '"';
      })
      .join(' ');
  }

  // 表示用: " を除去するだけ (検索欄の value 表示用)
  function stripQuotes(value) {
    if (typeof value !== 'string') return value;
    return value.replace(/"/g, '').trim();
  }

  // ① 検索フォーム submit 時: input.value を保持しつつ手動で URL 遷移
  //    + type=product を常に付与して検索対象を商品のみに限定 (page / article は除外)
  document.addEventListener(
    'submit',
    function (event) {
      var form = event.target;
      if (!isSearchForm(form)) return;

      var qInput = form.querySelector('input[name="q"]');
      if (!qInput) return;

      // form のデフォルト送信を阻止し、手動で URL を構築して遷移
      // (q をクォート化 + type=product 強制付与のため)
      event.preventDefault();
      event.stopPropagation();

      var action = form.getAttribute('action') || '/search';
      var formData = new FormData(form);
      formData.set('q', quoteTerms(qInput.value));
      formData.set('type', 'product');

      var params = new URLSearchParams();
      formData.forEach(function (value, key) {
        params.append(key, value);
      });

      window.location.href = action + '?' + params.toString();
    },
    true // capture phase で他ハンドラより先に処理
  );

  // ② (a) /search ページ訪問時の URL リライト (DOM 不要 → 即時実行でレンダリング前にリダイレクト)
  //        URL の q を quote 化 + type=product 強制付与 (商品のみ検索対象に限定)。
  //        本スクリプトは defer なし同期 <script> として読み込まれるため HTML パース中に実行され、
  //        最初のレンダリングが始まる前にリダイレクトが発生 → 2 重描画 (画面ちらつき) を防止。
  function rewriteSearchUrlIfNeeded() {
    var path = window.location.pathname;
    if (path !== '/search' && path.indexOf('/search') !== 0) return false;

    var params = new URLSearchParams(window.location.search);
    var q = params.get('q');
    if (!q) return false;

    var needsRewrite = false;

    var converted = quoteTerms(q);
    if (converted !== q) {
      params.set('q', converted);
      needsRewrite = true;
    }

    if (params.get('type') !== 'product') {
      params.set('type', 'product');
      needsRewrite = true;
    }

    if (!needsRewrite) return false;

    window.location.replace(path + '?' + params.toString() + window.location.hash);
    return true; // リダイレクト発生
  }

  var redirected = rewriteSearchUrlIfNeeded();

  // ② (b) /search ページで既に quote 済 URL の場合、検索欄 input の表示値から " を除去
  //        (見た目を「冷感」表示に整える / 内部クエリは "冷感" のまま)
  function normalizeSearchInputs() {
    var path = window.location.pathname;
    if (path !== '/search' && path.indexOf('/search') !== 0) return;

    var params = new URLSearchParams(window.location.search);
    var q = params.get('q');
    if (!q) return;

    var stripped = stripQuotes(q);
    if (stripped === q) return;

    var inputs = document.querySelectorAll('form input[name="q"]');
    inputs.forEach(function (input) {
      if (input.value === q) {
        input.value = stripped;
      }
    });
  }

  // リダイレクト発生時は次のページロードに任せる (DOM 操作不要)
  if (!redirected) {
    if (document.readyState === 'loading') {
      document.addEventListener('DOMContentLoaded', normalizeSearchInputs);
    } else {
      normalizeSearchInputs();
    }
  }
})();
